import { apiService } from './apiService';
import { PaymentMethod, type SalesFilters, type SalesTransaction, type SalesTransactionItem } from '../types/sales';

/** Raw row from GET /pos/transactions (same shape as POSTransaction response fields we need). */
interface PosTransactionRow {
  id: number;
  transaction_number: string;
  location_id: number;
  location_name?: string;
  sale_date: string;
  payment_method: string;
  total_amount: number | string;
  tax_amount?: number | string;
  discount_amount?: number | string;
  served_by: number;
  created_at: string;
  transaction_items?: Array<{
    id?: number;
    product_id: number;
    product_code?: string;
    product_name?: string;
    quantity: number;
    unit_price: number | string;
    total_price?: number | string;
  }>;
}

function toNum(v: unknown): number {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function mapPaymentMethod(raw: string): PaymentMethod {
  const u = String(raw || '').toUpperCase();
  if (u === 'CARD') return PaymentMethod.CARD;
  if (u === 'BANK_TRANSFER') return PaymentMethod.BANK_TRANSFER;
  return PaymentMethod.CASH;
}

function mapItems(items: PosTransactionRow['transaction_items']): SalesTransactionItem[] {
  if (!items?.length) return [];
  return items.map((it) => ({
    id: it.id,
    product_id: it.product_id,
    product_code: it.product_code,
    product_name: it.product_name,
    quantity: it.quantity,
    unit_price: toNum(it.unit_price),
    total_price: toNum(it.total_price ?? it.quantity * toNum(it.unit_price)),
  }));
}

function mapRow(txn: PosTransactionRow): SalesTransaction {
  const created = txn.created_at || new Date().toISOString();
  return {
    id: txn.id,
    transaction_number: txn.transaction_number,
    location_id: txn.location_id,
    sale_date: String(txn.sale_date || '').slice(0, 10),
    payment_method: mapPaymentMethod(txn.payment_method),
    total_amount: toNum(txn.total_amount),
    tax_amount: toNum(txn.tax_amount),
    discount_amount: toNum(txn.discount_amount),
    served_by: txn.served_by,
    created_at: created,
    updated_at: created,
    transaction_items: mapItems(txn.transaction_items),
  };
}

/**
 * List completed POS sales for the Sales Reports page (real data).
 * Uses /pos/transactions — same source as POS history and admin charts.
 */
export async function listSalesTransactions(filters: SalesFilters = {}): Promise<SalesTransaction[]> {
  const params: Record<string, string | number> = { limit: 1000 };
  if (filters.start_date) params.start_date = filters.start_date;
  if (filters.end_date) params.end_date = filters.end_date;
  if (filters.location_id != null) params.location_id = filters.location_id;

  if (filters.payment_method) {
    const pm = String(filters.payment_method);
    if (pm === 'CASH') params.payment_method = 'CASH';
    else if (pm === 'CARD') params.payment_method = 'CARD';
    else if (pm === 'BANK_TRANSFER') params.payment_method = 'BANK_TRANSFER';
  }

  const rows = await apiService.get<PosTransactionRow[]>('/pos/transactions', { params });
  let list = (Array.isArray(rows) ? rows : []).map(mapRow);

  // Product / loose text search (API search only matches transaction # / serial)
  const q = filters.search?.trim().toLowerCase();
  if (q) {
    list = list.filter(
      (s) =>
        s.transaction_number.toLowerCase().includes(q) ||
        s.transaction_items.some((i) => (i.product_name || '').toLowerCase().includes(q) || String(i.product_id).includes(q)),
    );
  }

  return list.sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
}

export async function createSalesTransaction(input: {
  location_id: number;
  sale_date: string;
  payment_method: PaymentMethod;
  tax_amount?: number;
  discount_amount?: number;
  items: Array<{ product_id: number; quantity: number; unit_price: number }>;
}): Promise<SalesTransaction> {
  const raw = await apiService.post<any>('/sales/', {
    location_id: input.location_id,
    sale_date: input.sale_date,
    payment_method: input.payment_method,
    tax_amount: input.tax_amount ?? 0,
    discount_amount: input.discount_amount ?? 0,
    items: input.items.map((it) => ({
      product_id: it.product_id,
      quantity: it.quantity,
      unit_price: it.unit_price,
    })),
  });
  const created = raw?.created_at || new Date().toISOString();
  return {
    id: raw.id,
    transaction_number: raw.transaction_number,
    location_id: raw.location_id,
    sale_date: String(raw.sale_date || '').slice(0, 10),
    payment_method: mapPaymentMethod(String(raw.payment_method)),
    total_amount: toNum(raw.total_amount),
    tax_amount: toNum(raw.tax_amount),
    discount_amount: toNum(raw.discount_amount),
    served_by: raw.served_by,
    created_at: created,
    updated_at: created,
    transaction_items: mapItems(raw.transaction_items),
  };
}
